import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Search, RefreshCw, Download, AlertCircle, CheckCircle, XCircle, Users, X } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { apiRequest } from '../utils/api';
import { useGroups } from '../utils/useGroups';
import { Button, JobStatusBadge, AlertBanner, PageHeader, Toggle, IllustrationNoData } from './ui';

const POLL_INTERVAL_MS = 4000;
const MAX_ROWS = 8000;
const MAX_FILE_SIZE_MB = 20;

// ── Mirror Lambda's HEADER_ALIASES exactly ────────────────────────────────────
const EMAIL_ALIASES    = new Set(['email', 'email address', 'emailaddress', 'e-mail']);
const FIRSTNAME_ALIASES = new Set(['firstname', 'first name', 'name']);
const LASTNAME_ALIASES  = new Set(['lastname', 'last name', 'surname', 'family name']);
const EMAIL_REGEX       = /(?:[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,})/i;

function normalizeHeader(h) {
  return (h || '').toString().trim().toLowerCase();
}

function detectColumn(headers, aliases) {
  return headers.find(h => aliases.has(normalizeHeader(h))) || null;
}

function rowHasEmail(row) {
  return Object.values(row).some(v => v && EMAIL_REGEX.test(String(v)));
}

// Columns the user can choose to carry through untouched.
// Anything the processor already maps into the template is excluded: every HEADER_ALIASES
// entry in the Lambda, plus the template's own column names. Keys are normalized the same
// way as the Lambda's normalize_key (lowercase, letters and digits only).
const normalizeKey = (v) => (v == null ? '' : String(v)).trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

const ALREADY_HANDLED_HEADERS = [
  // HEADER_ALIASES (processor Lambda)
  'firstname', 'first name', 'name',
  'lastname', 'last name', 'surname', 'family name',
  'middlename', 'middle name', 'middle initial',
  'address', 'address1', 'street address', 'street',
  'city', 'town',
  'state', 'state/province',
  'zip', 'zipcode', 'zip code', 'postal code', 'zip/postal code',
  'country', 'country/region name', 'country/region',
  'email', 'email address', 'emailaddress', 'e-mail',
  'phone', 'phone number', 'mobile', 'cell', 'telephone',
  'event', 'event name', 'program', 'program name',
  'event date', 'date', 'attendance date',
  'attended', 'attendance', 'status',
  'eventlookupid', 'eventid', 'event id',
  // Template column names (DEFAULT_TEMPLATE_COLUMNS)
  'BlackbaudID', 'Constituent Name', 'Lastname', 'Firstname', 'MiddleName',
  'AlternateLookupID', 'AlternateLookupIDType', 'EventLookupid', 'EventName', 'EventDate',
  'Attended', 'AddressType1', 'Address1', 'City1', 'State1', 'Zip1', 'Country1',
  'EmailType1', 'EmailAddress1', 'PhoneType', 'PhoneNumber', 'MismatchReason',
];
const SKIP_EXTRA = new Set(ALREADY_HANDLED_HEADERS.map(normalizeKey));

function getExtraCandidates(headers) {
  return headers.filter(h => h && !SKIP_EXTRA.has(normalizeKey(h)));
}

// ── Parse file client-side ────────────────────────────────────────────────────
async function parseFile(file) {
  const ext = file.name.split('.').pop().toLowerCase();

  if (ext === 'csv') {
    return new Promise(resolve => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: result => resolve({ headers: result.meta.fields || [], rows: result.data, error: null }),
        error:    err    => resolve({ headers: [], rows: [], error: err.message }),
      });
    });
  }

  if (ext === 'xlsx') {
    try {
      const buffer  = await file.arrayBuffer();
      const wb      = XLSX.read(buffer, { type: 'array' });
      const ws      = wb.Sheets[wb.SheetNames[0]];
      const rows    = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
      return { headers, rows, error: null };
    } catch (e) {
      return { headers: [], rows: [], error: e.message };
    }
  }

  return { headers: [], rows: [], error: 'Unsupported file type.' };
}

// ── Serialize rows back to CSV for upload ─────────────────────────────────────
function serializeToCSV(headers, rows) {
  return Papa.unparse({ fields: headers, data: rows.map(r => headers.map(h => r[h] ?? '')) });
}

// ── Validation pipeline ───────────────────────────────────────────────────────
function validateFile(file, headers, rows) {
  const errors   = [];
  const warnings = [];

  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    errors.push(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is ${MAX_FILE_SIZE_MB} MB.`);
  }
  if (rows.length === 0) {
    errors.push('This file has no data rows. Please check your export and try again.');
  }
  if (rows.length > MAX_ROWS) {
    errors.push(`This file has ${rows.length} rows. Maximum is ${MAX_ROWS}. Split the file and upload in batches.`);
  }

  const rowsWithEmail = rows.filter(rowHasEmail);
  if (rows.length > 0 && rowsWithEmail.length === 0) {
    errors.push('No email addresses were found anywhere in this file. Please check you\'ve exported the right file.');
  }

  if (errors.length === 0 && rows.length > 0) {
    const emailCoverage = rowsWithEmail.length / rows.length;
    if (emailCoverage < 0.5) {
      warnings.push(`Only ${Math.round(emailCoverage * 100)}% of rows contain an email address. Expect a high mismatch count.`);
    }

    const emails = [];
    rows.forEach(row => {
      Object.values(row).forEach(v => {
        const match = v && String(v).match(EMAIL_REGEX);
        if (match) emails.push(match[0].toLowerCase());
      });
    });
    const emailCounts = emails.reduce((acc, e) => { acc[e] = (acc[e] || 0) + 1; return acc; }, {});
    const dupeCount = Object.values(emailCounts).filter(c => c > 1).length;
    if (dupeCount > 0) {
      warnings.push(`${dupeCount} email address${dupeCount > 1 ? 'es appear' : ' appears'} on multiple rows. If you need one result per person, deduplicate by email before uploading.`);
    }
  }

  return { errors, warnings };
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  );
}

// ── Column mapping modal ──────────────────────────────────────────────────────
function ColumnMappingModal({ headers, onConfirm, onCancel }) {
  const [emailCol,     setEmailCol]     = useState('');
  const [firstNameCol, setFirstNameCol] = useState('');
  const [lastNameCol,  setLastNameCol]  = useState('');

  useEffect(() => {
    const guessEmail     = detectColumn(headers, EMAIL_ALIASES);
    const guessFirstName = detectColumn(headers, FIRSTNAME_ALIASES);
    const guessLastName  = detectColumn(headers, LASTNAME_ALIASES);
    if (guessEmail)     setEmailCol(guessEmail);
    if (guessFirstName) setFirstNameCol(guessFirstName);
    if (guessLastName)  setLastNameCol(guessLastName);
  }, [headers]);

  const canConfirm = emailCol.trim() !== '';

  function handleConfirm() {
    onConfirm({ emailCol, firstNameCol: firstNameCol || null, lastNameCol: lastNameCol || null });
  }

  const selectClass = [
    'w-full h-9 px-3 rounded-[var(--radius-lg)] border border-[var(--color-border)]',
    'text-sm text-[var(--color-text-primary)] bg-white',
    'focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)]',
  ].join(' ');

  const labelClass = 'block text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-modal)',
      background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: 'white', borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-xl)', width: '100%', maxWidth: 460,
        overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <p style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 3 }}>
              Map your columns
            </p>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              We couldn't automatically detect your column headers. Tell us which columns contain each field so we can match correctly.
            </p>
          </div>
          <button onClick={onCancel} style={{ color: 'var(--color-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className={labelClass}>
              Email address <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <select value={emailCol} onChange={e => setEmailCol(e.target.value)} className={selectClass}>
              <option value="">Select a column...</option>
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <p style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
              Required — this is how constituents are matched in TED.
            </p>
          </div>

          <div>
            <label className={labelClass}>First name <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional</span></label>
            <select value={firstNameCol} onChange={e => setFirstNameCol(e.target.value)} className={selectClass}>
              <option value="">Skip</option>
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <p style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
              Helps resolve cases where multiple people share an email address.
            </p>
          </div>

          <div>
            <label className={labelClass}>Last name <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional</span></label>
            <select value={lastNameCol} onChange={e => setLastNameCol(e.target.value)} className={selectClass}>
              <option value="">Skip</option>
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>

          {!canConfirm && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: 'var(--color-error-bg)', borderRadius: 'var(--radius-md)' }}>
              <AlertCircle size={13} style={{ color: 'var(--color-error)', flexShrink: 0 }} />
              <p style={{ fontSize: '0.72rem', color: 'var(--color-error)' }}>An email column is required to continue.</p>
            </div>
          )}
        </div>

        <div style={{ padding: '12px 24px 20px', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              height: 36, padding: '0 16px', borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-border)', background: 'white',
              fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              height: 36, padding: '0 20px', borderRadius: 'var(--radius-lg)',
              border: 'none',
              background: canConfirm ? 'var(--color-orange)' : 'var(--color-surface-alt)',
              fontSize: '0.8rem', fontWeight: 600,
              color: canConfirm ? 'white' : 'var(--color-text-muted)',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              transition: 'background 200ms',
            }}
          >
            Confirm &amp; Upload
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Extra columns modal ───────────────────────────────────────────────────────
function ExtraColumnsModal({ headers, onConfirm, onCancel }) {
  const [selected, setSelected] = useState([]);
  const toggle = (h) =>
    setSelected(prev => prev.includes(h) ? prev.filter(x => x !== h) : [...prev, h]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-modal)',
      background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: 'white', borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-xl)', width: '100%', maxWidth: 460,
        overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <p style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 3 }}>
            Any extra columns to carry through?
          </p>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            Selected columns are copied to the end of the review sheets exactly as uploaded. They are left off the UIF-ready sheets.
          </p>
        </div>
        <div style={{ padding: '12px 24px', maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {headers.map(h => (
            <label key={h} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--color-text-primary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.includes(h)} onChange={() => toggle(h)} />
              {h}
            </label>
          ))}
        </div>
        <div style={{ padding: '12px 24px 20px', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              height: 36, padding: '0 16px', borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-border)', background: 'white',
              fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selected)}
            style={{
              height: 36, padding: '0 20px', borderRadius: 'var(--radius-lg)',
              border: 'none', background: 'var(--color-orange)',
              fontSize: '0.8rem', fontWeight: 600, color: 'white',
              cursor: 'pointer',
            }}
          >
            {selected.length ? `Upload with ${selected.length} extra` : 'No extras — Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload section ────────────────────────────────────────────────────────────
const UploadSection = ({ onJobCreated }) => {
  const [file,              setFile]              = useState(null);
  const [dragging,          setDragging]          = useState(false);
  const [uploading,         setUploading]         = useState(false);
  const [errors,            setErrors]            = useState([]);
  const [warnings,          setWarnings]          = useState([]);
  const [dismissedWarnings, setDismissedWarnings] = useState([]);
  const [showMapping,       setShowMapping]       = useState(false);
  const [parsedData,        setParsedData]        = useState(null);
  const [needsMapping,      setNeedsMapping]      = useState(false);
  const [pendingUpload,     setPendingUpload]     = useState(null);

  const [singleEvent,  setSingleEvent]  = useState(false);
  const [eventLookupId, setEventLookupId] = useState('');
  const [eventName,    setEventName]    = useState('');
  const [eventDate,    setEventDate]    = useState('');
  const [markAttended, setMarkAttended] = useState(true);

  const fileInputRef = useRef();

  function reset() {
    setFile(null); setErrors([]); setWarnings([]);
    setDismissedWarnings([]); setParsedData(null);
    setNeedsMapping(false); setShowMapping(false);
    setPendingUpload(null);
    setSingleEvent(false);
    setEventLookupId(''); setEventName(''); setEventDate('');
    setMarkAttended(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function processFile(f) {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx'].includes(ext)) {
      setErrors(['Only CSV and XLSX files are supported.']);
      setFile(f);
      return;
    }
    setFile(f); setErrors([]); setWarnings([]); setDismissedWarnings([]);

    const { headers, rows, error: parseError } = await parseFile(f);
    if (parseError) {
      setErrors([`Could not read this file: ${parseError}. Please try exporting again.`]);
      return;
    }

    const { errors: validationErrors, warnings: validationWarnings } = validateFile(f, headers, rows);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setParsedData(null);
      return;
    }

    setParsedData({ headers, rows });
    setWarnings(validationWarnings);

    const hasEmailCol     = !!detectColumn(headers, EMAIL_ALIASES);
    const hasFirstNameCol = !!detectColumn(headers, FIRSTNAME_ALIASES);
    const hasLastNameCol  = !!detectColumn(headers, LASTNAME_ALIASES);
    if (!hasEmailCol || !hasFirstNameCol || !hasLastNameCol) {
      setNeedsMapping(true);
    }
  }

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    processFile(e.dataTransfer.files[0]);
  };
  const handleFileChange = (e) => processFile(e.target.files[0]);

  function applyEventFields(headers, rows) {
    if (!singleEvent) return { headers, rows };
    const eventHeaders = [];
    const eventValues  = {};
    if (eventLookupId.trim()) { eventHeaders.push('EventLookupid'); eventValues['EventLookupid'] = eventLookupId.trim(); }
    if (eventName.trim())     { eventHeaders.push('EventName');     eventValues['EventName']     = eventName.trim(); }
    if (eventDate.trim())     { eventHeaders.push('EventDate');     eventValues['EventDate']     = eventDate.trim(); }
    if (markAttended)         { eventHeaders.push('Attended');      eventValues['Attended']      = 'True'; }
    return {
      headers: [...headers, ...eventHeaders],
      rows:    rows.map(row => ({ ...row, ...eventValues })),
    };
  }

  async function doUpload(headers, rows, filename, passthroughColumns = []) {
    setUploading(true);
    try {
      const { headers: finalHeaders, rows: finalRows } = applyEventFields(headers, rows);
      const csv         = serializeToCSV(finalHeaders, finalRows);
      const csvFilename = filename.replace(/\.xlsx$/i, '.csv');
      const blob        = new Blob([csv], { type: 'text/csv' });

      const { job_id, upload_url } = await apiRequest('/id-matching/jobs', {
        method: 'POST',
        body: JSON.stringify({ filename: csvFilename, passthrough_columns: passthroughColumns }),
      });

      const uploadRes = await fetch(upload_url, {
        method: 'PUT', body: blob,
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      if (!uploadRes.ok) throw new Error('Upload to storage failed. Please try again.');

      reset();
      onJobCreated(job_id);
    } catch (e) {
      console.error(e);
      setErrors([e.message || 'Upload failed. Please try again.']);
    } finally { setUploading(false); }
  }

  // Ask about extra columns first; skip the question when there is nothing to offer.
  function queueUpload(headers, rows) {
    if (getExtraCandidates(headers).length === 0) doUpload(headers, rows, file.name);
    else setPendingUpload({ headers, rows });
  }

  function handleUploadClick() {
    if (!parsedData) return;
    if (needsMapping) setShowMapping(true);
    else queueUpload(parsedData.headers, parsedData.rows);
  }

  function handleMappingConfirm({ emailCol, firstNameCol, lastNameCol }) {
    setShowMapping(false);
    const { headers, rows } = parsedData;
    const renamedHeaders = headers.map(h => {
      if (h === emailCol)     return 'Email';
      if (h === firstNameCol) return 'Firstname';
      if (h === lastNameCol)  return 'Lastname';
      return h;
    });
    const renamedRows = rows.map(row => {
      const newRow = {};
      headers.forEach((h, i) => { newRow[renamedHeaders[i]] = row[h]; });
      return newRow;
    });
    queueUpload(renamedHeaders, renamedRows);
  }

  function handleMappingCancel() { reset(); }

  function handleExtraConfirm(cols) {
    const { headers, rows } = pendingUpload;
    setPendingUpload(null);
    doUpload(headers, rows, file.name, cols);
  }

  const activeWarnings = warnings.filter((_, i) => !dismissedWarnings.includes(i));
  const canUpload      = file && errors.length === 0 && parsedData !== null && !uploading;

  const inputClass = [
    'w-full h-9 px-3 rounded-[var(--radius-lg)]',
    'border border-[var(--color-border)] bg-white text-sm text-[var(--color-text-primary)]',
    'focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)]',
    'placeholder-[var(--color-text-muted)] transition-colors',
  ].join(' ');

  const labelClass = 'block text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5';

  return (
    <>
      <div className="bg-white rounded-[var(--radius-xl)] border border-[var(--color-border)] shadow-[var(--shadow-xs)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--color-border)]">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Upload Registration File</h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">CSV or XLSX · max {MAX_ROWS} rows · max {MAX_FILE_SIZE_MB} MB</p>
        </div>
        <div className="p-5 space-y-3">

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={[
              'relative border-2 border-dashed rounded-[var(--radius-lg)] p-6 text-center transition-colors',
              uploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              dragging
                ? 'border-[var(--color-orange)] bg-[var(--color-orange-light)]'
                : errors.length > 0
                  ? 'border-[var(--color-error-border)] bg-[var(--color-error-bg)]'
                  : file && parsedData
                    ? 'border-[var(--color-success-border)] bg-[var(--color-success-bg)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-navy)] hover:bg-[var(--color-surface)]',
            ].join(' ')}
          >
            {file && !uploading && (
              <button
                onClick={(e) => { e.stopPropagation(); reset(); }}
                title="Clear file and start over"
                style={{
                  position: 'absolute', top: 8, right: 8,
                  width: 24, height: 24, borderRadius: '50%',
                  background: 'var(--color-surface-alt)',
                  border: '1px solid var(--color-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'var(--color-text-muted)', flexShrink: 0,
                }}
              >
                <X size={12} />
              </button>
            )}

            {file && parsedData && errors.length === 0 ? (
              <CheckCircle size={20} className="mx-auto mb-2" style={{ color: 'var(--color-success)' }} />
            ) : errors.length > 0 ? (
              <XCircle size={20} className="mx-auto mb-2" style={{ color: 'var(--color-error)' }} />
            ) : (
              <Upload size={20} className="mx-auto mb-2 text-[var(--color-text-muted)]" />
            )}
            {file ? (
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">{file.name}</p>
            ) : (
              <p className="text-sm font-medium text-[var(--color-text-secondary)]">
                Drag and drop your file here, or click to browse
              </p>
            )}
            {file && parsedData && errors.length === 0 && (
              <p className="text-xs mt-1" style={{ color: 'var(--color-success)' }}>
                {parsedData.rows.length} rows · {parsedData.headers.length} columns
                {needsMapping && ' · column mapping required'}
              </p>
            )}
            {!file && <p className="text-xs text-[var(--color-text-muted)] mt-1">CSV or XLSX</p>}
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileChange} />
          </div>

          {errors.length > 0 && (
            <div style={{ padding: '10px 14px', background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {errors.map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <AlertCircle size={13} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: '0.75rem', color: 'var(--color-error)', lineHeight: 1.5 }}>{e}</p>
                </div>
              ))}
              <button onClick={reset} style={{ alignSelf: 'flex-start', fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-error)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2, textDecoration: 'underline' }}>
                Choose a different file
              </button>
            </div>
          )}

          {activeWarnings.map((w, i) => {
            const originalIndex = warnings.indexOf(w);
            return (
              <div key={i} style={{ padding: '10px 14px', background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <AlertCircle size={13} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: '0.75rem', color: 'var(--color-warning)', lineHeight: 1.5 }}>{w}</p>
                </div>
                <button onClick={() => setDismissedWarnings(prev => [...prev, originalIndex])} style={{ color: 'var(--color-warning)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
                  <X size={13} />
                </button>
              </div>
            );
          })}

          {parsedData && errors.length === 0 && (
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>Single event file?</p>
                  <p style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                    Add event details to every row in the output automatically.
                  </p>
                </div>
                <Toggle checked={singleEvent} onChange={setSingleEvent} />
              </div>

              {singleEvent && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Event Lookup ID</label>
                      <input type="text" value={eventLookupId} onChange={e => setEventLookupId(e.target.value)} placeholder="e.g. 8-10012934" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Event Name</label>
                    <input type="text" value={eventName} onChange={e => setEventName(e.target.value)} placeholder="e.g. UIAA Homecoming Gala" className={inputClass} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 2 }}>
                    <div>
                      <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>Mark all as Attended</p>
                      <p style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                        {markAttended ? 'Attended will be set to True for all rows.' : 'Leave off to set attendance manually in TED later.'}
                      </p>
                    </div>
                    <Toggle checked={markAttended} onChange={setMarkAttended} />
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleUploadClick}
            disabled={!canUpload}
            style={{
              width: '100%', height: 40,
              borderRadius: 'var(--radius-lg)', border: 'none',
              background: canUpload ? 'var(--color-orange)' : 'var(--color-surface-alt)',
              color: canUpload ? 'white' : 'var(--color-text-muted)',
              fontSize: '0.875rem', fontWeight: 600,
              cursor: canUpload ? 'pointer' : 'not-allowed',
              transition: 'background 200ms',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {uploading ? (
              <><Spinner className="h-4 w-4" /> Uploading...</>
            ) : needsMapping && parsedData ? (
              'Map Columns & Upload'
            ) : (
              'Upload File'
            )}
          </button>
        </div>
      </div>

      {showMapping && parsedData && (
        <ColumnMappingModal
          headers={parsedData.headers}
          onConfirm={handleMappingConfirm}
          onCancel={handleMappingCancel}
        />
      )}

      {pendingUpload && (
        <ExtraColumnsModal
          headers={getExtraCandidates(pendingUpload.headers)}
          onConfirm={handleExtraConfirm}
          onCancel={() => setPendingUpload(null)}
        />
      )}
    </>
  );
};

// ── Instructions panel ────────────────────────────────────────────────────────
function InstructionsPanel() {
  const steps = [
    { number: '1', title: 'Upload your list', body: 'Export your event registration list as CSV or XLSX and upload it here. No special formatting needed — the tool finds email addresses automatically. If your file has other columns, you can choose any to carry through untouched.' },
    { number: '2', title: 'Download results', body: "You'll get an Excel file with four sheets. Matched and Mismatch are for your review and include any extra columns you carried through, plus a MismatchReason column on Mismatch. Matched - UIF Ready and Mismatch - UIF Ready contain only the TED columns." },
    { number: '3', title: 'Submit to UIF', body: 'Export the two UIF Ready sheets as CSV and attach both to a Footprints ticket. Ask UIF to match or create the records on the Mismatch sheet.' },
    { number: '4', title: 'Optional: add event details', body: 'Before submitting, you can add Event ID, Event Name, and Attended columns directly in the Excel output.' },
  ];

  return (
    <div style={{ width: 320, flexShrink: 0, borderRight: '1px solid var(--color-border)', background: 'white', display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '20px 24px 28px' }}>
      <p style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
        How it works
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {steps.map((step, i) => (
          <div key={step.number} style={{ display: 'flex', gap: 12, position: 'relative' }}>
            {i < steps.length - 1 && (
              <div style={{ position: 'absolute', left: 11, top: 28, bottom: 0, width: 1, background: 'var(--color-border)' }} />
            )}
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--color-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, marginTop: 1 }}>
              <span style={{ fontSize: '0.6rem', fontWeight: 700, color: 'white' }}>{step.number}</span>
            </div>
            <div style={{ paddingBottom: 20, flex: 1 }}>
              <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 3 }}>{step.title}</p>
              <p style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>{step.body}</p>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 4, padding: '10px 12px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)' }}>
        <p style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          Questions? Contact{' '}
          <a href="mailto:smpatil4@illinois.edu" style={{ color: 'var(--color-orange)', textDecoration: 'none', fontWeight: 600 }}>Sid</a>
        </p>
      </div>
    </div>
  );
}

// ── Quick match ───────────────────────────────────────────────────────────────
const QuickMatch = () => {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const handleSearch = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setLoading(true); setResult(null); setError('');
    try {
      const data = await apiRequest('/id-matching/quick-match', {
        method: 'POST',
        body: JSON.stringify({ email: trimmed }),
      });
      setResult(data);
    } catch {
      setError('Lookup failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-[var(--radius-xl)] border border-[var(--color-border)] shadow-[var(--shadow-xs)] overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--color-border)]">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Quick Email Lookup</h2>
        <p className="text-xs text-[var(--color-text-muted)] mt-0.5">Look up a single constituent by email</p>
      </div>
      <div className="p-5 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none" />
            <input
              type="email" value={email}
              onChange={(e) => { setEmail(e.target.value); setResult(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="Enter email address..."
              className="w-full pl-9 pr-3 h-10 border border-[var(--color-border)] rounded-[var(--radius-lg)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)] text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] bg-white"
            />
          </div>
          <Button variant="primary" size="md" loading={loading} disabled={!email.trim()} onClick={handleSearch}>
            Look Up
          </Button>
        </div>
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)', borderRadius: 'var(--radius-lg)' }}>
            <AlertCircle size={13} style={{ color: 'var(--color-error)', flexShrink: 0 }} />
            <p style={{ fontSize: '0.75rem', color: 'var(--color-error)' }}>{error}</p>
          </div>
        )}
        {result && (
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-3 text-sm">
            {result.status === 'matched' && (
              <div className="flex items-start gap-2 text-[var(--color-success)]">
                <CheckCircle size={15} className="mt-0.5 shrink-0" />
                <div>
                  <span className="font-semibold">Match found</span>
                  <div className="mt-1 text-[var(--color-text-secondary)]">
                    <code className="bg-[var(--color-surface-alt)] px-1.5 py-0.5 rounded text-xs font-mono">{result.aux_id}</code>
                    <span className="ml-2">{result.first_name} {result.key_name}</span>
                  </div>
                </div>
              </div>
            )}
            {result.status === 'no_match' && (
              <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
                <XCircle size={15} />
                <span>No constituent found for <strong className="text-[var(--color-text-primary)]">{result.email}</strong></span>
              </div>
            )}
            {result.status === 'duplicate' && (
              <div>
                <div className="flex items-center gap-2 text-[var(--color-warning)] mb-2">
                  <AlertCircle size={15} />
                  <span className="font-semibold">Multiple constituents share this email</span>
                </div>
                <ul className="space-y-1.5">
                  {result.matches.map((m) => (
                    <li key={m.aux_id} className="flex items-center gap-2 text-[var(--color-text-secondary)] text-xs">
                      <code className="bg-[var(--color-surface-alt)] px-1.5 py-0.5 rounded font-mono">{m.aux_id}</code>
                      <span>{m.first_name} {m.key_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Duration helper ───────────────────────────────────────────────────────────
// Computes human-readable processing time from job timestamps.
function formatDuration(submittedAt, completedAt) {
  if (!submittedAt || !completedAt) return null;
  const ms = new Date(completedAt) - new Date(submittedAt);
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

// ── Job row ───────────────────────────────────────────────────────────────────
const JobRow = ({ job, onDownload }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try { await onDownload(job.job_id); }
    finally { setDownloading(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  const duration = formatDuration(job.submitted_at, job.completed_at);
  const cellClass = 'px-4 py-3 text-sm text-[var(--color-text-secondary)]';

  return (
    <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)] transition-colors">
      <td className={`${cellClass} font-medium text-[var(--color-text-primary)] max-w-xs truncate`} title={job.filename}>
        {job.filename}
      </td>
      {job._showUsername && <td className={cellClass}>{job.netid ?? job.username}</td>}
      <td className={cellClass}>{formatDate(job.submitted_at)}</td>
      <td className={`${cellClass} text-center`}>
        {job.status === 'complete'
          ? <span className="font-semibold text-[var(--color-success)]">{job.matched_count ?? '—'}</span>
          : '—'}
      </td>
      <td className={`${cellClass} text-center`}>
        {job.status === 'complete'
          ? <span className={`font-semibold ${job.mismatch_count > 0 ? 'text-[var(--color-warning)]' : 'text-[var(--color-text-muted)]'}`}>
              {job.mismatch_count ?? '—'}
            </span>
          : '—'}
      </td>
      <td className={cellClass}>
        <JobStatusBadge status={job.status} />
        {/* Duration shown under badge for complete jobs — feeds analytics */}
        {job.status === 'complete' && duration && (
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            completed in {duration}
          </p>
        )}
        {job.status === 'failed' && job.error_message && (
          <p className="text-xs text-[var(--color-error)] mt-0.5 max-w-xs truncate" title={job.error_message}>
            {job.error_message}
          </p>
        )}
      </td>
      <td className={`${cellClass} text-right`}>
        {job.status === 'complete' && (
          <button
            onClick={handleDownload} disabled={downloading}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-navy)] hover:text-[var(--color-orange)] transition-colors disabled:opacity-50"
          >
            {downloading ? <Spinner /> : <Download size={16} />}
            Download
          </button>
        )}
        {job.status === 'processing' && (
          <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
            <Spinner /> Working...
          </span>
        )}
      </td>
    </tr>
  );
};

// ── Job history ───────────────────────────────────────────────────────────────
const JobHistory = ({ newJobId, isAdmin }) => {
  const [jobs,      setJobs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showAll,   setShowAll]   = useState(false);
  const pollRef    = useRef(null);
  const showAllRef = useRef(false);
  const STUCK_THRESHOLD_MS = 2 * 60 * 1000;

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const qs = showAllRef.current ? '?all=true' : '';
      try {
        const data    = await apiRequest(`/id-matching/jobs${qs}`);
        const fetched = data.jobs || [];
        setJobs(fetched);
        if (!fetched.some(j => j.status === 'processing')) stopPolling();
      } catch (e) { console.error('Poll error', e); }
    }, POLL_INTERVAL_MS);
  }, []);

  const fetchJobs = useCallback(async (all = false) => {
    try {
      const qs   = all ? '?all=true' : '';
      const data = await apiRequest(`/id-matching/jobs${qs}`);
      const fetched = data.jobs || [];
      setJobs(fetched);
      setLoadError(null);
      if (fetched.some(j => j.status === 'processing')) startPolling();
      else stopPolling();
      return fetched;
    } catch (e) {
      console.error('Failed to fetch jobs', e);
      setLoadError('Could not load jobs. Check your connection and try refreshing.');
      return [];
    } finally { setLoading(false); }
  }, [startPolling]);

  useEffect(() => { fetchJobs(showAll); return stopPolling; }, []); // eslint-disable-line
  useEffect(() => { if (!newJobId) return; fetchJobs(showAllRef.current); }, [newJobId, fetchJobs]);

  const handleDownload = async (job_id) => {
    try {
      const data = await apiRequest(`/id-matching/jobs/${job_id}/download`);
      window.open(data.download_url, '_blank');
    } catch {
      setJobs(prev => prev.map(j => j.job_id === job_id ? { ...j, _downloadError: true } : j));
      setTimeout(() => setJobs(prev => prev.map(j => j.job_id === job_id ? { ...j, _downloadError: false } : j)), 4000);
    }
  };

  const handleToggleAll = () => {
    const next = !showAll;
    showAllRef.current = next;
    setShowAll(next);
    fetchJobs(next);
  };

  const now = Date.now();
  const displayJobs = jobs.map(j => ({
    ...j,
    _showUsername: showAll,
    _stuck: j.status === 'processing' && j.submitted_at &&
      (now - new Date(j.submitted_at).getTime()) > STUCK_THRESHOLD_MS,
  }));

  const thClass = 'px-4 py-3 text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider text-left';

  return (
    <div className="bg-white rounded-[var(--radius-xl)] border border-[var(--color-border)] shadow-[var(--shadow-xs)] overflow-hidden" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between" style={{ flexShrink: 0 }}>
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {showAll ? 'All Jobs' : 'My Jobs'}
        </h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={handleToggleAll}
              className={[
                'inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-[var(--radius-md)] border transition-colors',
                showAll
                  ? 'bg-[var(--color-navy)] text-white border-[var(--color-navy)]'
                  : 'bg-white text-[var(--color-navy)] border-[var(--color-navy)] hover:bg-[var(--color-surface)]',
              ].join(' ')}
            >
              <Users size={12} />
              {showAll ? 'Showing All Users' : 'Show All Users'}
            </button>
          )}
          <button
            onClick={() => fetchJobs(showAll)}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors p-1.5 rounded-[var(--radius-md)] hover:bg-[var(--color-surface)]"
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
            <Spinner className="h-4 w-4" /> Loading jobs...
          </div>
        </div>
      ) : loadError ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
          <AlertCircle size={28} style={{ color: 'var(--color-error)' }} />
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>{loadError}</p>
          <button onClick={() => fetchJobs(showAll)} style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-orange)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            Try again
          </button>
        </div>
      ) : displayJobs.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '32px 24px' }}>
          <IllustrationNoData width={180} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>No jobs yet</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Upload a registration file above and your results will appear here.</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto" style={{ flex: 1, overflowY: 'auto' }}>
          <table className="w-full text-left">
            <thead className="bg-[var(--color-surface)] border-b border-[var(--color-border)]" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th className={thClass}>File</th>
                {showAll && <th className={thClass}>Submitted By</th>}
                <th className={thClass}>Submitted</th>
                <th className={`${thClass} text-center`}>Matched</th>
                <th className={`${thClass} text-center`}>Mismatch</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Result</th>
              </tr>
            </thead>
            <tbody>
              {displayJobs.map(job => (
                <React.Fragment key={job.job_id}>
                  <JobRow job={job} onDownload={handleDownload} />
                  {job._stuck && (
                    <tr>
                      <td colSpan={showAll ? 7 : 6} style={{ padding: '6px 16px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 'var(--radius-md)' }}>
                          <AlertCircle size={13} style={{ color: 'var(--color-warning)', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.72rem', color: 'var(--color-warning)' }}>
                            This job is taking longer than expected. Try refreshing — if it stays stuck, contact Sid.
                          </p>
                          <button onClick={() => fetchJobs(showAll)} style={{ marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-warning)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
                            Refresh
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {job._downloadError && (
                    <tr>
                      <td colSpan={showAll ? 7 : 6} style={{ padding: '6px 16px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)', borderRadius: 'var(--radius-md)' }}>
                          <AlertCircle size={13} style={{ color: 'var(--color-error)', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.72rem', color: 'var(--color-error)' }}>Download failed. Please try again.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const IDMatching = () => {
  const [newJobId, setNewJobId] = useState(null);
  const { groups } = useGroups();
  const isAdmin = groups?.some(g => ['admin', 'superadmin'].includes(g));

  const handleJobCreated = (job_id) => {
    setNewJobId(job_id);
    setTimeout(() => {
      document.getElementById('job-history')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white">
      <PageHeader
        title="ID Matching"
        subtitle="Match constituents to their TED ID using their emails"
      />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <InstructionsPanel />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--color-surface)', padding: '24px 24px 0' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" style={{ marginBottom: 20, flexShrink: 0 }}>
            <UploadSection onJobCreated={handleJobCreated} />
            <QuickMatch />
          </div>
          <div id="job-history" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingBottom: 24 }}>
            <JobHistory newJobId={newJobId} isAdmin={isAdmin} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IDMatching;